import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronRight,
  Clapperboard,
  Clock3,
  Database,
  Eye,
  FileText,
  FileVideo,
  Film,
  ImagePlus,
  Info,
  Languages,
  Settings2,
  Star,
  Subtitles,
  Trash2,
  Tv,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useSession } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { useCategories } from './MediaLibraryPage';
import { MoviePlayer, movieBody, sourceInfo, type Movie } from './VodPage';

const EMPTY: Movie = {
  id: '',
  title: '',
  description: null,
  year: null,
  durationMin: null,
  rating: null,
  genres: [],
  audioLanguages: [],
  subtitleLanguages: [],
  posterAssetId: null,
  backdropAssetId: null,
  sourceUrl: '',
  featured: false,
  enabled: true,
  categories: [],
};

export function VodEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['vod', 'items'], queryFn: () => api.get<{ items: Movie[] }>('/vod/titles'), enabled: id !== 'new' });
  if (!id || id === 'new') return <Editor initial={null} />;
  if (list.isLoading) return <Loading />;
  const m = list.data?.items.find((x) => x.id === id);
  if (!m)
    return (
      <div className="card empty">
        This movie was not found.{' '}
        <button className="btn small" onClick={() => navigate('/content/vod')}>
          Back to movies
        </button>
      </div>
    );
  return <Editor key={m.id} initial={m} />;
}

function Card({ icon, title, children, aside }: { icon: ReactNode; title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="card">
      <div className="row" style={{ gap: 10 }}>
        <span className="pkg-head-icon sm">{icon}</span>
        <strong style={{ fontSize: 16, flex: 1 }}>{title}</strong>
        {aside}
      </div>
      <div className="fe-body">{children}</div>
    </div>
  );
}

/** Chips with an inline input: Enter or comma adds. */
function Tags({ value, onChange, placeholder, disabled }: { value: string[]; onChange: (v: string[]) => void; placeholder: string; disabled: boolean }) {
  const [text, setText] = useState('');
  const add = () => {
    const t = text.trim().replace(/,$/, '');
    if (t && !value.includes(t)) onChange([...value, t]);
    setText('');
  };
  return (
    <div className="allergen-box tags-box">
      {value.map((t) => (
        <span key={t} className="chip brand-chip">
          {t}
          {!disabled && (
            <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} aria-label={`Remove ${t}`}>
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <input
          value={text}
          placeholder={value.length ? '' : placeholder}
          onChange={(e) => (e.target.value.endsWith(',') ? (setText(e.target.value), setTimeout(add)) : setText(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            } else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={add}
        />
      )}
    </div>
  );
}

function ArtPicker({ label, hint, url, wide, disabled, onChange }: { label: string; hint: string; url: string | null; wide?: boolean; disabled: boolean; onChange: (a: { id: string; url: string } | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: (f: File) => api.upload<{ id: string; url: string }>('/assets', f),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['assets'] });
      onChange(a);
    },
  });
  return (
    <div className="art-picker">
      <button type="button" className={`art-thumb${wide ? ' wide' : ''}`} disabled={disabled || upload.isPending} onClick={() => input.current?.click()}>
        {url ? <img src={url} alt="" /> : <ImagePlus size={22} />}
      </button>
      <div>
        <div className="camp-label">{label}</div>
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <button type="button" className="btn btn-soft small" disabled={disabled || upload.isPending} onClick={() => input.current?.click()}>
            {upload.isPending ? 'Uploading…' : 'Choose file'}
          </button>
          {url && (
            <button type="button" className="btn small" disabled={disabled} onClick={() => onChange(null)}>
              Remove
            </button>
          )}
        </div>
        <div className="camp-hint" style={{ marginTop: 8 }}>
          {hint}
        </div>
        <ErrorBox error={upload.error} />
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/** Reads the length of a local video file in the browser (minutes), or null. */
function videoMinutes(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    const src = URL.createObjectURL(file);
    let settled = false;
    const end = (n: number | null) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(src);
      resolve(n);
    };
    v.preload = 'metadata';
    v.onloadedmetadata = () => end(isFinite(v.duration) && v.duration > 0 ? Math.max(1, Math.round(v.duration / 60)) : null);
    v.onerror = () => end(null);
    setTimeout(() => end(null), 8000);
    v.src = src;
  });
}

const MAX_MOVIE = 4 * 1024 ** 3;
const size = (b: number) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${Math.round(b / 1024 ** 2)} MB`);

/** Upload an MP4 to this server; the movie then plays from it. */
function VideoUpload({ onUploaded }: { onUploaded: (url: string, minutes: number | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [job, setJob] = useState<{ name: string; bytes: number; p: number; abort: () => void } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const start = async (file: File) => {
    setError(null);
    setDone(null);
    if (!/\.(mp4|m4v)$/i.test(file.name) && file.type !== 'video/mp4') return setError(new Error('Choose an MP4 file (H.264 video, AAC audio).'));
    if (file.size > MAX_MOVIE) return setError(new Error(`The file is ${size(file.size)}; the limit is 4 GB.`));
    const minutes = await videoMinutes(file);
    const up = api.uploadWithProgress<{ url: string }>('/assets/movie', file, (p) => setJob((j) => (j ? { ...j, p } : j)));
    setJob({ name: file.name, bytes: file.size, p: 0, abort: up.abort });
    try {
      const a = await up.done;
      onUploaded(new URL(a.url, location.origin).href, minutes);
      setDone(file.name);
    } catch (e) {
      setError(e);
    } finally {
      setJob(null);
    }
  };
  useEffect(() => {
    if (!job) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [job]);
  return (
    <div className="video-upload">
      {job ? (
        <>
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap', gap: 10 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong>Uploading {job.name}</strong>{' '}
              <span className="muted">
                {size(job.bytes * job.p)} of {size(job.bytes)}
              </span>
            </span>
            <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
              <strong>{Math.floor(job.p * 100)}%</strong>
              <button type="button" className="btn small" onClick={job.abort}>
                Cancel
              </button>
            </span>
          </div>
          <div className="upload-bar">
            <span style={{ width: `${job.p * 100}%` }} />
          </div>
          <div className="camp-hint">{job.p >= 1 ? 'Finishing on the server…' : 'Keep this page open until the upload finishes.'}</div>
        </>
      ) : (
        <div className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
          <span className="video-upload-ic">
            <Upload size={20} />
          </span>
          <span style={{ flex: 1 }}>
            <strong style={{ display: 'block' }}>Upload a video file</strong>
            <span className="muted" style={{ fontSize: 12.5 }}>
              MP4 (H.264 video, AAC audio), up to 4 GB. It is stored on this server and the URL below is filled in.
            </span>
          </span>
          <button type="button" className="btn btn-soft" onClick={() => input.current?.click()}>
            <Upload size={15} /> Choose video
          </button>
        </div>
      )}
      {done && !job && (
        <div className="badge ok" style={{ marginTop: 10 }}>
          <Check size={12} /> {done} uploaded. Save the movie to use it.
        </div>
      )}
      <ErrorBox error={error} />
      <input
        ref={input}
        type="file"
        accept="video/mp4,.mp4,.m4v"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void start(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function Editor({ initial }: { initial: Movie | null }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const url = useAssetUrls();
  const cats = useCategories('vod');
  const [m, setM] = useState<Movie>(initial ?? EMPTY);
  const [art, setArt] = useState<{ poster?: string | null; backdrop?: string | null }>({});
  const [dirty, setDirty] = useState(false);
  const set = (patch: Partial<Movie>) => {
    setM((v) => ({ ...v, ...patch }));
    setDirty(true);
  };
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['vod', 'items'] });
    setDirty(false);
    navigate('/content/vod');
  };
  const save = useMutation({
    mutationFn: () => {
      const body = movieBody({ ...m, title: m.title.trim(), sourceUrl: m.sourceUrl.trim(), description: m.description?.trim() || null, rating: m.rating?.trim() || null });
      return initial ? api.put(`/vod/titles/${initial.id}`, body) : api.post('/vod/titles', body);
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api.delete(`/vod/titles/${initial!.id}`), onSuccess: done });
  const leave = () => (dirty ? ask('Leave without saving? Your changes to this movie will be lost.', () => navigate('/content/vod'), { action: 'Leave', danger: true }) : navigate('/content/vod'));
  const editable = can('ADMIN');
  const poster = art.poster !== undefined ? art.poster : url(m.posterAssetId);
  const backdrop = art.backdrop !== undefined ? art.backdrop : url(m.backdropAssetId);
  const src = sourceInfo(m.sourceUrl);
  const urlOk = /^https?:\/\/\S+$/.test(m.sourceUrl.trim());
  const catList = cats.data?.items ?? [];
  const catIds = m.categories.map((c) => c.categoryId);
  const num = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.round(Number(v))) || null);
  const row = (icon: ReactNode, label: string, value: ReactNode) => (
    <div className="fac-detail-row">
      {icon}
      <span>{label}</span>
      <span className="fac-detail-val">{value}</span>
    </div>
  );

  return (
    <>
      <div className="crumbs">
        <button type="button" onClick={leave}>
          VOD
        </button>
        <ChevronRight size={14} />
        <button type="button" onClick={leave}>
          Movies
        </button>
        <ChevronRight size={14} />
        <strong>{initial ? 'Edit movie' : 'Add movie'}</strong>
      </div>
      <PageHeader
        title={initial ? `Edit movie · ${initial.title}` : 'Add movie'}
        sub="The details, artwork, video file and TV visibility of this movie."
        actions={
          <>
            <button className="btn" onClick={leave}>
              <ArrowLeft size={16} /> Back to movies
            </button>
            {editable && (
              <button className="btn primary" disabled={!m.title.trim() || !urlOk || save.isPending} onClick={() => save.mutate()}>
                <Check size={16} /> {save.isPending ? 'Saving…' : initial ? 'Save changes' : 'Add movie'}
              </button>
            )}
          </>
        }
      />
      <ErrorBox error={save.error ?? remove.error} />
      <div className="stat-strip">
        <div className="stat-tile">
          <span className={`stat-tile-icon ${m.enabled ? 'green' : 'grey'}`}>
            <Tv size={22} />
          </span>
          <span style={{ flex: 1 }}>
            <span className="stat-tile-label">Visible on TV</span>
            <span className="stat-tile-value" style={{ fontSize: 20 }}>
              {m.enabled ? 'Yes' : 'No'}
            </span>
          </span>
          <Toggle on={m.enabled} onChange={(v) => editable && set({ enabled: v })} label="Visible on TV" />
        </div>
        <div className="stat-tile">
          <span className="stat-tile-icon amber">
            <Star size={22} />
          </span>
          <span style={{ flex: 1 }}>
            <span className="stat-tile-label">Featured</span>
            <span className="stat-tile-value" style={{ fontSize: 20 }}>
              {m.featured ? 'Yes' : 'No'}
            </span>
          </span>
          <Toggle on={m.featured} onChange={(v) => editable && set({ featured: v })} label="Featured" />
        </div>
        <div className="stat-tile">
          <span className="stat-tile-icon violet">
            <Film size={22} />
          </span>
          <span>
            <span className="stat-tile-label">Categories</span>
            <span className="stat-tile-value" style={{ fontSize: 20 }}>
              {catIds.length}
            </span>
            <span className="stat-tile-sub">{catIds.length ? 'Packages with these show it' : 'No package shows it yet'}</span>
          </span>
        </div>
      </div>

      <div className="vod-edit">
        <div className="stack">
          <Card icon={<FileText size={18} />} title="Basic information">
            <div className="vod-grid">
              <label className="camp-field span2">
                <span className="camp-label">
                  Title <span style={{ color: '#dc2626' }}>*</span>
                </span>
                <input value={m.title} maxLength={200} disabled={!editable} onChange={(e) => set({ title: e.target.value })} autoFocus={!initial} />
              </label>
              <label className="camp-field">
                <span className="camp-label">Year</span>
                <input type="number" min={1900} max={2100} value={m.year ?? ''} disabled={!editable} onChange={(e) => set({ year: num(e.target.value) })} />
              </label>
              <label className="camp-field">
                <span className="camp-label">Duration (minutes)</span>
                <input type="number" min={1} value={m.durationMin ?? ''} disabled={!editable} onChange={(e) => set({ durationMin: num(e.target.value) })} />
              </label>
              <label className="camp-field">
                <span className="camp-label">Rating</span>
                <input value={m.rating ?? ''} maxLength={20} disabled={!editable} onChange={(e) => set({ rating: e.target.value })} placeholder="PG-13" />
              </label>
            </div>
            <div className="camp-grid" style={{ marginTop: 0 }}>
              <div className="camp-field">
                <span className="camp-label">Audio languages</span>
                <Tags value={m.audioLanguages} disabled={!editable} onChange={(audioLanguages) => set({ audioLanguages })} placeholder="English, Arabic…" />
              </div>
              <div className="camp-field">
                <span className="camp-label">Subtitles</span>
                <Tags value={m.subtitleLanguages} disabled={!editable} onChange={(subtitleLanguages) => set({ subtitleLanguages })} placeholder="None" />
              </div>
            </div>
            <label className="camp-field">
              <span className="camp-label">Synopsis</span>
              <textarea value={m.description ?? ''} maxLength={4000} disabled={!editable} onChange={(e) => set({ description: e.target.value })} style={{ minHeight: 90 }} />
              <span className="fe-count">{(m.description ?? '').length}/4000</span>
            </label>
            <div className="camp-field">
              <span className="camp-label">Genres</span>
              <Tags value={m.genres} disabled={!editable} onChange={(genres) => set({ genres })} placeholder="Thriller, Romance…" />
            </div>
            <div className="camp-field">
              <span className="camp-label">Categories</span>
              <span className="row" style={{ gap: 6 }}>
                {catList.map((c) => {
                  const on = catIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`pick-chip${on ? ' on' : ''}`}
                      disabled={!editable}
                      onClick={() => set({ categories: on ? m.categories.filter((x) => x.categoryId !== c.id) : [...m.categories, { categoryId: c.id }] })}
                    >
                      {c.name}
                    </button>
                  );
                })}
                {catList.length === 0 && <span className="muted">Create categories first (Categories tab).</span>}
              </span>
              <span className="camp-hint">Packages unlock movies by category (Content › Packages).</span>
            </div>
          </Card>

          <Card icon={<ImagePlus size={18} />} title="Media & artwork">
            <div className="camp-grid" style={{ marginTop: 0 }}>
              <ArtPicker
                label="Poster (2:3)"
                hint="Portrait, e.g. 600 × 900. JPG or PNG."
                url={poster}
                disabled={!editable}
                onChange={(a) => {
                  set({ posterAssetId: a?.id ?? null });
                  setArt((v) => ({ ...v, poster: a?.url ?? null }));
                }}
              />
              <ArtPicker
                label="Backdrop (16:9)"
                hint="Landscape, e.g. 1920 × 1080. Used on the movie page and the hero carousel."
                url={backdrop}
                wide
                disabled={!editable}
                onChange={(a) => {
                  set({ backdropAssetId: a?.id ?? null });
                  setArt((v) => ({ ...v, backdrop: a?.url ?? null }));
                }}
              />
            </div>
          </Card>

          <Card icon={<Database size={18} />} title="Playback source">
            {editable && <VideoUpload onUploaded={(src, minutes) => set({ sourceUrl: src, ...(!m.durationMin && minutes ? { durationMin: minutes } : {}) })} />}
            <label className="camp-field">
              <span className="camp-label">
                Video URL (MP4, H.264/AAC) <span style={{ color: '#dc2626' }}>*</span>
              </span>
              <input value={m.sourceUrl} maxLength={2000} disabled={!editable} onChange={(e) => set({ sourceUrl: e.target.value })} placeholder="http://10.0.0.5/media/vod/film.mp4" className="mono" />
              <span className={`camp-hint${m.sourceUrl && !urlOk ? ' err' : ''}`}>
                {m.sourceUrl && !urlOk ? 'Enter a full http:// or https:// address.' : 'From the hotel server, NAS or Edge cache. The TVs play it directly.'}
              </span>
            </label>
            <div className="vod-grid three">
              <span className="camp-field">
                <span className="camp-label">Source</span>
                <span className="ro-field">{src.origin}</span>
              </span>
              <span className="camp-field">
                <span className="camp-label">File name</span>
                <span className="ro-field">{src.file}</span>
              </span>
              <span className="camp-field">
                <span className="camp-label">Format</span>
                <span className="ro-field">{src.format}</span>
              </span>
            </div>
          </Card>
        </div>

        <div className="stack vod-side">
          <div className="card">
            <div className="row" style={{ gap: 8, fontWeight: 700, color: 'var(--brand)' }}>
              <Eye size={18} /> Preview on TV
            </div>
            <MoviePlayer key={m.sourceUrl} src={urlOk ? m.sourceUrl : null} image={backdrop ?? poster ?? null} style={{ marginTop: 12 }} />
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'nowrap' }}>
              <h3 style={{ margin: 0, flex: 1, minWidth: 0 }}>{m.title || 'Untitled'}</h3>
              {m.featured && (
                <span className="badge warn">
                  <Star size={11} /> Featured
                </span>
              )}
              <span className={`state-pill ${m.enabled ? 'online' : 'offline'}`}>
                <span className="state-dot" /> {m.enabled ? 'Visible' : 'Hidden'}
              </span>
            </div>
            <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
              {[m.year, m.durationMin && `${m.durationMin} min`, m.rating].filter(Boolean).join(' • ')}
            </div>
            {m.genres.length > 0 && (
              <div className="row" style={{ gap: 6, marginTop: 8 }}>
                {m.genres.map((g) => (
                  <span key={g} className="chip">
                    {g}
                  </span>
                ))}
              </div>
            )}
            {m.description && <p className="muted fac-desc">{m.description}</p>}
          </div>

          <div className="card">
            <div className="row" style={{ gap: 8, fontWeight: 700 }}>
              <Info size={18} color="var(--brand)" /> Movie details
            </div>
            <div className="fac-detail-list" style={{ borderTop: 0 }}>
              {row(<CalendarDays size={16} />, 'Year', m.year ?? '—')}
              {row(<Clock3 size={16} />, 'Duration', m.durationMin ? `${m.durationMin} minutes` : '—')}
              {row(<Star size={16} />, 'Rating', m.rating || '—')}
              {row(<Database size={16} />, 'Source', src.origin)}
              {row(<FileVideo size={16} />, 'File name', src.file)}
              {row(<Film size={16} />, 'Format', src.format)}
              {row(<Languages size={16} />, 'Audio', m.audioLanguages.join(', ') || '—')}
              {row(<Subtitles size={16} />, 'Subtitles', m.subtitleLanguages.join(', ') || 'None')}
              {row(<Settings2 size={16} />, 'Categories', catIds.length)}
            </div>
          </div>

          {initial && editable && (
            <div className="card danger-zone">
              <Trash2 size={20} />
              <span style={{ flex: 1 }}>
                <strong>Danger zone</strong>
                <span className="muted" style={{ display: 'block', fontSize: 12.5 }}>
                  Removes the movie from the library and the TVs. The video file is not touched.
                </span>
              </span>
              <button className="btn danger-outline" onClick={() => ask(`Delete ${initial.title}? It disappears from the TVs.`, () => remove.mutate())}>
                Delete movie
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
